import { logger } from '@gem/modules/logger';
import { RemoteStorageTasks } from '@gem/modules/remote-storage';

import Tasks from 'APP/Tasks';
import GlobalTasks from 'APP/Tasks';
import { RefreshTokenInitiator } from 'APP/Tasks/authorization/login/login.types';
import { logout } from 'APP/Tasks/authorization/logout/logout';
import { initRealtimeEvents } from 'APP/Tasks/realtimeEvent/realTimeEvents';
import { uniffiInitAsync } from 'APP/libs/mls-rs-wasm';
import { ILoginSuccess } from 'APP/model/auth/authModel.types';
import { e2eeModel } from 'APP/model/model';
import { analyticsClient } from 'APP/packages/analytics/client/AnalyticsClient';
import { broadcastAuthSuccess } from 'APP/packages/bus/adapters/auth/auth';
import { Features } from 'APP/packages/features/Features';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { storage } from 'APP/packages/storage';
import { setSseConfig } from 'APP/packages/streamApi';
import { wsApiConnect } from 'APP/wsApi/wsApi';
import { setWebsocketConfig } from 'APP/wsApi/wsApi.config';
import { MlsStorageService } from 'SERVICES/mlsStorageService/MlsStorageService';
import { WebCryptoService } from 'SERVICES/webCryptoService/WebCryptoService';
import Entities from 'STORE';

import { handleClientConfig } from './handleClientConfig/handleClientConfig';
import { refreshToken } from './refreshToken';

export async function initProject(
  loginResponse: ILoginSuccess,
  initStore = true,
  token = loginResponse.accessToken,
  skipE2ee?: boolean
): Promise<boolean> {
  await Tasks.config.updateHostConfig();

  if (!loginResponse.user || !ProjectUrls.isCorrectConfig) {
    return false;
  }

  storage.refreshToken.set(loginResponse.refreshToken);
  setWebsocketConfig({ token });
  setSseConfig({ token });
  wsApiConnect(loginResponse.accessToken, async () => {
    const result = await refreshToken(false, RefreshTokenInitiator.WsApp);

    if (result.isSuccess && result.token) {
      return result.token;
    } else {
      return logout().then(() => null);
    }
  });

  analyticsClient.setUserConfig(loginResponse.user);
  handleClientConfig(loginResponse.clientConfig);

  Entities.authStore.setDeleted(false);
  Entities.authStore.setSession(loginResponse.accessToken);
  Entities.authStore.setStreamToken(token);
  RemoteStorageTasks.loadRemoteStorageCredentials();

  Entities.authStore.setIsGuestCallSession(false);
  Entities.usersStore.addMe({
    ...loginResponse.user!,
    deepLink: '',
  });

  broadcastAuthSuccess(loginResponse.user?.id);

  Entities.usersStore.Me.setIsValid(true);

  try {
    if (initStore && !skipE2ee) {
      await uniffiInitAsync();
      await MlsStorageService.initMlsGroupStorage(loginResponse.user.id);
      await WebCryptoService.init(loginResponse.user.id);
    }

    const keyPackagesOnClientCount = MlsStorageService.getKeyPackagesCount();
    let keyPackagesServerCount = skipE2ee
      ? await e2eeModel.getKeyPackagesCount()
      : loginResponse.deviceKeyPackagesCount;

    if (
      keyPackagesServerCount &&
      keyPackagesServerCount.availableCount > keyPackagesOnClientCount
    ) {
      await e2eeModel.clearCurrentDeviceE2eeData();
      keyPackagesServerCount = await e2eeModel.getKeyPackagesCount();
    }

    if (keyPackagesServerCount?.missingCount) {
      Tasks.e2ee.uploadKeyPackages(keyPackagesServerCount!.missingCount);
    }
  } catch (error) {
    logger.get('auth').error('init project', error);
  }

  if (!initStore) {
    return true;
  }

  Tasks.appearance.loadChatBackgrounds();
  Tasks.appearance.loadChatBackgroundsCustom();

  // Нельзя инициализировать фича флаги паралльльно рендеру приложения,
  // так как фича флаги в том числе влияют на навигацию
  await Tasks.features.initFeatures();

  Promise.all([
    Tasks.workspace.loadWorkspace(),
    Tasks.users.getFriends(),
    Tasks.userSettings.getPrivacySettings(),
    Tasks.group.initGroups(),
  ]).then(async () => {
    if (Features.isUserGiftsEnabled()) {
      await Tasks.userSettings.getRewardsProgress();
    }

    Tasks.app.onboarding.initOnboarding();

    if (Features.isSpacesEnabled()) {
      Tasks.space.initSpaces();
    }

    await Tasks.pushNotifications.initNotifications();

    if (Features.isNewsfeedEnabled()) {
      Tasks.newsfeed.preloadNewsfeed({ cursor: null });
    }

    GlobalTasks.countriesPhoneCodes.getCountriesPhoneCodes();

    if (Features.isContactsEnabled()) {
      await Tasks.authorization.getAccountCountry(loginResponse.user?.phone || '');
    }

    await initRealtimeEvents();
  });

  return true;
}
